import { ApiQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { ModuleRendering, SlotRendering } from '@frontend/common-types';
import { DefaultBadge } from '@frontend/components/badge';
import { DefaultPrimaryButton } from '@frontend/components/common';
import { Module } from '@frontend/module/types';
import { Spot } from '@frontend/spot/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { Module as ModuleComponent } from '../module/module.component';
import { ZoomControls } from '../zoom-controls/zoom-controls.component';
import { useSpotLayoutController } from './spot-layout.controller';

export interface SpotLayoutStyles<TSlot extends SlotRendering = SlotRendering> {
    getSlotColor?: (slot: TSlot) => string;
    isSlotDisabled?: (slot: TSlot) => boolean;
}

interface SpotLayoutProps<TModule extends ModuleRendering = ModuleRendering, TSlot extends SlotRendering = SlotRendering> {
    spot?: Spot;
    module?: Module;
    onSlotClick?: (clickedSlot: TSlot) => void;
    onModuleClick?: (module: TModule) => void;
    fetchSpotData: (arg?: ApiQueryParams<string | number>) => Promise<PaginatedResponse<TModule>>;
    fetchModuleData: (accountId: string, spotId: string, moduleId: string) => Promise<{ results: TSlot[] }>;
    showLabels?: boolean;
    showLegend?: boolean;
    enableZoom?: boolean;
    enableZoomControls?: boolean;
    className?: string;
    renderSingleModule?: boolean;
    styles?: SpotLayoutStyles<TSlot>;
    slot?: {
        hoverElement?: (slot: TSlot) => React.ReactNode;
        renderElement?: (slot: TSlot) => React.ReactNode;
    };
}

export const SpotLayout = <TModule extends ModuleRendering = ModuleRendering, TSlot extends SlotRendering = SlotRendering>(
    props: SpotLayoutProps<TModule, TSlot>
) => {
    const {
        containerRef,
        isContainerMeasured,
        data,
        selectedModuleId,
        modules,
        modulesWithSlots,
        nonRenderableModuleCount,
        currentModule,
        scale,
        centerOffsetX,
        centerOffsetY,
        containerWidth,
        containerHeight,
        handleModuleExpand,
        handleNavigateModule
    } = useSpotLayoutController<TModule, TSlot>({
        spot: props.spot,
        module: props.module,
        renderSingleModule: props.renderSingleModule,
        fetchSpotData: props.fetchSpotData,
        fetchModuleData: props.fetchModuleData
    });

    if (!data || data.length === 0) {
        return (
            <div className={`flex h-full items-center justify-center ${props.className}`}>
                <p className='text-brand-muted'>
                    <FormattedMessage
                        id='spots.layout.no-spot-layout-data-available'
                        defaultMessage='No SPOT layout data available.'
                    />
                </p>
            </div>
        );
    }

    const layoutContent = (
        <div
            className={selectedModuleId ? 'relative mx-auto' : 'relative'}
            style={{
                width: `${containerWidth}px`,
                height: `${containerHeight}px`
            }}>
            {modulesWithSlots.map((module) => (
                <ModuleComponent
                    key={module.id}
                    module={module}
                    scale={scale}
                    offsetX={centerOffsetX}
                    offsetY={centerOffsetY}
                    onSlotClick={props.onSlotClick}
                    onModuleClick={props.onModuleClick}
                    showLabels={props.showLabels}
                    spotId={props.spot?.id}
                    onModuleExpand={handleModuleExpand}
                    isExpanded={selectedModuleId === module.id}
                    slotHoverElement={props.slot?.hoverElement}
                    getSlotColor={props.styles?.getSlotColor}
                    isSlotDisabled={props.styles?.isSlotDisabled}
                    slotRenderElement={props.slot?.renderElement}
                />
            ))}
        </div>
    );

    return (
        <div className={`flex h-full min-h-0 w-full min-w-0 flex-col gap-4 ${props.className}`}>
            {/* Header with spot name */}
            {props.spot && selectedModuleId && !props.renderSingleModule && (
                <div className='border-border-default flex items-center justify-between border-t border-solid p-4'>
                    <div className='flex w-full items-center gap-4'>
                        <DefaultPrimaryButton
                            text='Go back'
                            onClick={() => handleModuleExpand(selectedModuleId)}
                        />
                        <div className='text-foreground-default flex w-full gap-2 text-xl font-semibold'>
                            <p>{props.spot.name}</p>
                            <p>/</p>
                            <p>{currentModule?.index}</p>
                        </div>
                    </div>
                </div>
            )}
            {props.renderSingleModule && selectedModuleId && modules.length > 1 && (
                <div className='flex items-center justify-center gap-4'>
                    <button
                        onClick={() => handleNavigateModule('prev')}
                        className='bg-brand-secondary text-background-surface rounded p-2 font-semibold hover:opacity-90'
                        aria-label='Previous module'>
                        ←
                    </button>
                    <span className='text-foreground-default font-semibold'>{currentModule?.index ?? ''}</span>
                    <button
                        onClick={() => handleNavigateModule('next')}
                        className='bg-brand-secondary text-background-surface rounded p-2 font-semibold hover:opacity-90'
                        aria-label='Next module'>
                        →
                    </button>
                </div>
            )}
            {nonRenderableModuleCount > 0 && (
                <div className='px-4 pt-4'>
                    <DefaultBadge
                        id='non-renderable-modules-notice'
                        styles={{ container: { color: 'bg-brand-muted' } }}
                        label={
                            <FormattedMessage
                                id='spot-rendering.non-renderable-modules-hidden'
                                description='Badge above the SPOT layout when virtual modules are omitted from the geometric view'
                                defaultMessage='{count, plural, one {# module is not shown on this layout} other {# modules are not shown on this layout}}'
                                values={{ count: nonRenderableModuleCount }}
                            />
                        }
                    />
                </div>
            )}
            <div className='relative flex min-h-0 min-w-0 flex-1 flex-col'>
                {props.enableZoom && props.enableZoomControls ? (
                    <TransformWrapper
                        centerOnInit
                        centerZoomedOut
                        initialScale={1}
                        minScale={0.5}
                        maxScale={3}
                        limitToBounds
                        wheel={{ step: 0.1 }}
                        panning={{ disabled: false }}
                        disablePadding={true}>
                        <>
                            <div className='border-border-default bg-background-surface border-x border-b p-5'>
                                <div className='flex items-center justify-between gap-4'>
                                    <span className='text-foreground-default min-w-0 truncate text-2xl font-semibold'>{props.spot?.name}</span>
                                    <ZoomControls />
                                </div>
                            </div>
                            <div
                                ref={containerRef}
                                className={`min-h-0 min-w-0 flex-1 overflow-auto rounded-md p-4 ${selectedModuleId ? 'flex items-center justify-center' : ''}`}>
                                <TransformComponent
                                    key={isContainerMeasured ? 'measured' : 'measuring'}
                                    wrapperClass='h-full w-full'
                                    wrapperStyle={{ width: '100%', height: '100%' }}>
                                    {isContainerMeasured ? layoutContent : null}
                                </TransformComponent>
                            </div>
                        </>
                    </TransformWrapper>
                ) : (
                    <div
                        ref={containerRef}
                        className={`min-h-0 min-w-0 flex-1 overflow-auto rounded-md p-4 ${selectedModuleId ? 'flex items-center justify-center' : ''}`}>
                        {isContainerMeasured && layoutContent}
                    </div>
                )}
            </div>
        </div>
    );
};
